import { MapPin, 
  Camera, 
  Mountain, 
  Utensils, 
  Tent, 
  Users,
  Fuel,
  ChevronDown,
  Eye } from '@/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/components'
import { Button } from '@/ui/components'
import type { POIType } from '@/pages/itinerary-builder/utils/waypointTypeStyles'

export interface WaypointTypeOption {
  type: POIType
  label: string
  icon: typeof MapPin
  description?: string
}

// Main itinerary stops (Route Stops)
const ROUTE_STOP_TYPES: WaypointTypeOption[] = [
  {
    type: null,
    label: 'Route Stop',
    icon: MapPin,
    description: 'Main trip itinerary stop'
  },
  {
    type: 'campsite',
    label: 'Campsite',
    icon: Tent,
    description: 'Camping location'
  },
  {
    type: 'meetup_spot',
    label: 'Meet Here',
    icon: Users,
    description: 'Group meeting point'
  },
  {
    type: 'gas',
    label: 'Fuel',
    icon: Fuel,
    description: 'Gas / fuel stop'
  }
]

// Detours (side trips)
const DETOUR_TYPES: WaypointTypeOption[] = [
  {
    type: 'viewpoint',
    label: 'Photo Spot',
    icon: Camera,
    description: 'Scenic photo location'
  },
  {
    type: 'custom',
    label: 'Overlook',
    icon: Eye,
    description: 'Scenic viewpoint'
  },
  {
    type: 'trailhead',
    label: 'Hike',
    icon: Mountain,
    description: 'Hiking trail or activity'
  },
  {
    type: 'custom',
    label: 'Lunch Stop',
    icon: Utensils,
    description: 'Meal break location'
  }
]

interface WaypointTypeDropdownProps {
  onSelect: (type: POIType, customLabel?: string) => void
  trigger?: React.ReactNode
  align?: 'start' | 'end' | 'center'
  side?: 'top' | 'bottom' | 'left' | 'right'
}

export function WaypointTypeDropdown({
  onSelect,
  trigger,
  align = 'end',
  side = 'bottom'
}: WaypointTypeDropdownProps) {
  const handleSelect = (option: WaypointTypeOption) => {
    if (option.type === 'custom') {
      // For custom types (Lunch Stop, Overlook), we need to pass the custom label
      onSelect('custom', option.label)
    } else {
      onSelect(option.type)
    }
  }

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="flex items-center gap-2">
      <MapPin className="w-4 h-4" />
      Add Stop
      <ChevronDown className="w-4 h-4" />
    </Button>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || defaultTrigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="w-56">
        {/* Main Itinerary Stops (Route Stops) */}
        {ROUTE_STOP_TYPES.map((option) => {
          const Icon = option.icon
          return (
            <DropdownMenuItem
              key={option.label}
              onClick={() => handleSelect(option)}
              className="flex items-center gap-2 group"
            >
              <Icon className="w-4 h-4 group-data-[highlighted]:text-accent-foreground" />
              <div className="flex flex-col">
                <span className="font-medium group-data-[highlighted]:text-accent-foreground">{option.label}</span>
                {option.description && (
                  <span className="text-xs text-muted-foreground group-data-[highlighted]:text-accent-foreground/90">{option.description}</span>
                )}
              </div>
            </DropdownMenuItem>
          )
        })}
        
        <DropdownMenuSeparator />
        
        {/* Detours */}
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Detours
        </div>
        
        {DETOUR_TYPES.map((option) => {
          const Icon = option.icon
          return (
            <DropdownMenuItem
              key={option.label}
              onClick={() => handleSelect(option)}
              className="flex items-center gap-2 group"
            >
              <Icon className="w-4 h-4 group-data-[highlighted]:text-accent-foreground" />
              <div className="flex flex-col">
                <span className="font-medium group-data-[highlighted]:text-accent-foreground">{option.label}</span>
                {option.description && (
                  <span className="text-xs text-muted-foreground group-data-[highlighted]:text-accent-foreground/90">{option.description}</span>
                )}
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
